import React from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { MetaPixelRouteTracker } from 'src/components/MetaPixelRouteTracker';
import { ROUTE_SOURCES } from 'src/constants/routeSources';
import { AdminRedirect } from 'src/pages/AdminRedirect';
import { AppInstalling } from 'src/pages/AppInstalling';
import { ConnectorAccounts } from 'src/pages/ConnectorAccounts';
import { ConnectorMetrics } from 'src/pages/ConnectorMetrics';
import { Connectors } from 'src/pages/Connectors';
import { CreateCustomFormula } from 'src/pages/CustomFormula/CreateCustomFormula';
import { EditCustomFormula } from 'src/pages/CustomFormula/EditCustomFormula';
import CustomReport from 'src/pages/CustomReport';
import { ResetPassword } from 'src/pages/ResetPassword';
import { SetNewPassword } from 'src/pages/SetNewPassword';
import { Settings } from 'src/pages/Settings';
import { SignIn } from 'src/pages/SignIn';
import { SignUp } from 'src/pages/SignUp';
import { PrivateTemplate } from 'src/templates/PrivateTemplate';
import { PublicTemplate } from 'src/templates/PublicTemplate';
import { ConnectorType } from 'src/utils/gql/types';
import { Terms } from 'src/pages/Terms';
import { Privacy } from 'src/pages/Privacy';
import { DataDeletion } from 'src/pages/DataDeletion';
import { TeamManagement } from 'src/pages/TeamManagement';
import { AcceptInvite } from 'src/pages/AcceptInvite';
import Support from 'src/pages/Support';
import { DatabaseConnector } from 'src/pages/DatabaseConnector';
import { MagentoConnector } from 'src/pages/MagentoConnector';
import BillingDashboard from 'src/pages/Admin/BillingDashboard';
import { Home } from 'src/pages/Home';
import Inventory from 'src/pages/Inventory';
import RestockedPage from 'src/pages/InventoryReplenishments/index';
import Pareto from 'src/pages/Pareto';
import CategoryAnalysis from 'src/pages/CategoryAnalysis';
import Bundles from 'src/pages/Bundles';
import KPI from 'src/pages/KPI';
import FinancePage from 'src/pages/Finance';
import RetentionPage from 'src/pages/Retention';
import AiDeckStudio from 'src/pages/AiDeckStudio';
import { LandingAdv } from 'src/pages/LandingAdv';
import { LandingAdvThankYou } from 'src/pages/LandingAdvThankYou';
import { BlogHome } from 'src/pages/BlogHome';
import { BlogPostPage } from 'src/pages/BlogPost';

import { GPT } from './components/Dashboard';

const appRoutes = [
  {
    path: '/admin',
    element: <AdminRedirect />,
  },
  {
    path: `/${ROUTE_SOURCES.FACEBOOK_AUTH_CALLBACK}`,
    element: <AppInstalling type={ConnectorType.FACEBOOK} />,
  },
  {
    path: `/${ROUTE_SOURCES.GOOGLE_ADS_AUTH_CALLBACK}`,
    element: <AppInstalling type={ConnectorType.GOOGLE_ADS} />,
  },
  {
    path: `/${ROUTE_SOURCES.GOOGLE_ANALYTICS_AUTH_CALLBACK}`,
    element: <AppInstalling type={ConnectorType.GOOGlE_ANALYTICS4} />,
  },
  {
    path: `/${ROUTE_SOURCES.GOOGLE_SEARCH_CONSOLE_AUTH_CALLBACK}`,
    element: <AppInstalling type={ConnectorType.GOOGLE_SEARCH_CONSOLE} />,
  },
  {
    element: <PublicTemplate />,
    children: [
      {
        path: '/',
        element: <Home />,
      },
      {
        path: `/${ROUTE_SOURCES.BLOG}`,
        element: <BlogHome locale="en" />,
      },
      {
        path: `/${ROUTE_SOURCES.BLOG_IT}`,
        element: <BlogHome locale="it" />,
      },
      {
        path: `/${ROUTE_SOURCES.BLOG}/:slug`,
        element: <BlogPostPage locale="en" />,
      },
      {
        path: `/${ROUTE_SOURCES.BLOG_IT}/:slug`,
        element: <BlogPostPage locale="it" />,
      },
      {
        path: `/${ROUTE_SOURCES.REGISTER}`,
        element: <SignUp />,
      },
      {
        path: `/${ROUTE_SOURCES.LOGIN}`,
        element: <SignIn />,
      },
      {
        path: `/${ROUTE_SOURCES.FORGOT_PASSWORD}`,
        element: <ResetPassword />,
      },
      {
        path: `/${ROUTE_SOURCES.RESET_PASSWORD}`,
        element: <SetNewPassword />,
      },
      {
        path: `/${ROUTE_SOURCES.TERMS}`,
        element: <Terms />,
      },
      {
        path: `/${ROUTE_SOURCES.PRIVACY}`,
        element: <Privacy />,
      },
      {
        path: `/${ROUTE_SOURCES.DATA_DELETION}`,
        element: <DataDeletion />,
      },
      {
        path: `/${ROUTE_SOURCES.LANDING_ADV}`,
        element: <LandingAdv />,
      },
      {
        path: `/${ROUTE_SOURCES.LANDING_ADV}/:lang`,
        element: <LandingAdv />,
      },
      {
        path: `/${ROUTE_SOURCES.LANDING_ADV_THANK_YOU}`,
        element: <LandingAdvThankYou />,
      },
      {
        path: `/${ROUTE_SOURCES.LANDING_ADV_THANK_YOU}/:lang`,
        element: <LandingAdvThankYou />,
      },
      {
        path: `/${ROUTE_SOURCES.ACCEPT_INVITE}`,
        element: <AcceptInvite />,
      },
    ],
  },
  {
    element: <PrivateTemplate />,
    children: [
      {
        path: `/${ROUTE_SOURCES.CONNECTORS}`,
        element: <Connectors />,
      },
      {
        path: `/${ROUTE_SOURCES.CONNECTORS}/${ROUTE_SOURCES.ACCOUNTS}`,
        element: <ConnectorAccounts />,
      },
      {
        path: `/${ROUTE_SOURCES.CONNECTORS}/${ROUTE_SOURCES.METRICS}`,
        element: <ConnectorMetrics />,
      },
      {
        path: `/${ROUTE_SOURCES.SHOPIFY_AUTH_CALLBACK}`,
        element: <AppInstalling type={ConnectorType.SHOPIFY} />,
      },
      {
        path: `/${ROUTE_SOURCES.CONVERSATION}`,
        element: <GPT />,
      },
      {
        path: `/${ROUTE_SOURCES.DASHBOARD}`,
        element: <Navigate to={`/${ROUTE_SOURCES.CONVERSATION}`} replace />,
      },
      {
        path: `/${ROUTE_SOURCES.SETTINGS}`,
        element: <Settings />,
      },
      {
        path: `/${ROUTE_SOURCES.SETTINGS}/${ROUTE_SOURCES.CREATE_FORMULA}`,
        element: <CreateCustomFormula />,
      },
      {
        path: `/${ROUTE_SOURCES.SETTINGS}/${ROUTE_SOURCES.EDIT_FORMULA}/:formulaId`,
        element: <EditCustomFormula />,
      },
      {
        path: `/${ROUTE_SOURCES.CUSTOM_REPORT}`,
        element: <CustomReport />,
      },
      {
        path: `/${ROUTE_SOURCES.SUPPORT}`,
        element: <Support />,
      },
      {
        path: `/${ROUTE_SOURCES.TEAM_MANAGEMENT}`,
        element: <TeamManagement />,
      },
      {
        path: '/database-connector',
        element: <DatabaseConnector />,
      },
      {
        path: '/magento-connector',
        element: <MagentoConnector />,
      },
      {
        path: '/admin/billing',
        element: <BillingDashboard />,
      },
      {
        path: `/${ROUTE_SOURCES.INVENTORY}`,
        element: <Inventory />,
      },
      {
        path: `/${ROUTE_SOURCES.INVENTORY_RESTOCKED}`,
        element: <RestockedPage />,
      },
      {
        path: `/${ROUTE_SOURCES.PRODUCTS_ANALYSIS}`,
        element: <Pareto />,
      },
      {
        path: `/${ROUTE_SOURCES.CATEGORY_ANALYSIS}`,
        element: <CategoryAnalysis />,
      },
      {
        path: `/${ROUTE_SOURCES.BUNDLES}`,
        element: <Bundles />,
      },
      {
        path: `/${ROUTE_SOURCES.RETENTION}`,
        element: <RetentionPage />,
      },
      {
        path: `/${ROUTE_SOURCES.KPI}`,
        element: <KPI />,
      },
      {
        path: `/${ROUTE_SOURCES.FINANCE}`,
        element: <FinancePage />,
      },
      {
        path: '/labs/deck-gamma',
        element: <Navigate to={`/${ROUTE_SOURCES.AI_DECK_STUDIO}`} replace />,
      },
      {
        path: `/${ROUTE_SOURCES.AI_DECK_STUDIO}`,
        element: <AiDeckStudio />,
      },
    ],
  },
];

export const router = createBrowserRouter([
  { element: <MetaPixelRouteTracker />, children: appRoutes },
]);
